import { defineStore } from 'pinia';
import i18n from '@/plugins/vue-i18n';
import { useToastStore } from '@/stores/utils/toast.store';
import { Claim, CompleteClaim, UpdateClaim } from '@/types/api/sites.types';
import {
  completeClaim,
  deleteClaim,
  downloadClaim,
  editClaim,
  getClaimDetails,
  initiateClaim,
} from '@/api/draws.service';
import router from '@/router';
import { exportFile } from '@/common/utils/general.utils';
import { selectedOrgId } from '@/common/utils/role.utils';

const { t } = i18n.global;

export const useDrawStore = defineStore('draw', {
  state: () => ({
    claim: {} as Claim,
    loadingModalOpen: false as boolean,
    downloadedClaim: null as Blob | null,
  }),

  actions: {
    async initiateDrawClaim(id: string, name: string) {
      const toastStore = useToastStore();

      const response = await initiateClaim(id);

      if (response.status === 200) {
        toastStore.showToast(
          'success',
          t('sites.claims.successToast.title'),
          t('sites.claims.successToast.content'),
        );

        this.claim = response.data.data;
        if (this.loadingModalOpen) {
          await router.push({
            name: 'generate-claim-sheet',
            params: { drawId: id, drawName: name, organisationId: selectedOrgId.value },
          });
        }
      }

      if (this.loadingModalOpen) {
        this.toggleLoadingModal(false);
      }
    },

    async updateClaim(id: string, data: UpdateClaim) {
      const response = await editClaim(id, data);
      this.claim = response.data.data;
    },

    async getClaimDetails(id: string) {
      const response = await getClaimDetails(id);
      this.claim = response.data.data;
    },

    async completeClaim(id: string, data: CompleteClaim) {
      const toastStore = useToastStore();

      const response = await completeClaim(id, data);

      if (response.status === 200) {
        toastStore.showToast(
          'success',
          t('sites.claims.claimTable.completeSuccessToast.title'),
          t('sites.claims.claimTable.completeSuccessToast.content'),
        );

        this.claim = response.data.data;
        await router.push({
          name: 'boq',
          hash: `#${t('sites.boq.tabs.draws')}`,
          params: { organisationId: selectedOrgId.value },
        });
      }
    },

    async deleteClaim(id: string) {
      const toastStore = useToastStore();

      const response = await deleteClaim(id);

      if (response.status === 204) {
        toastStore.showToast(
          'success',
          t('sites.claims.claimTable.deleteSuccessToast.title'),
          t('sites.claims.claimTable.deleteSuccessToast.content'),
        );

        await router.push({
          name: 'boq',
          hash: `#${t('sites.boq.tabs.draws')}`,
          params: { organisationId: selectedOrgId.value },
        });
      }
    },

    async downloadClaim(id: string, drawName: string) {
      const toastStore = useToastStore();

      try {
        const response = await downloadClaim(id);
        const contentType =
          (response.headers['content-type'] as string) ?? 'application/octet-stream';

        this.downloadedClaim = new Blob([response.data], {
          type: contentType,
        });

        exportFile(this.downloadedClaim, contentType, `${drawName} - Claim`);

        toastStore.showToast(
          'success',
          t('sites.claims.claimTable.downloadSuccessToast.title', {
            claim: t('sites.claims.claim'),
          }),
          t('sites.claims.claimTable.downloadSuccessToast.content', {
            claim: t('sites.claims.claim'),
          }),
        );
      } catch (error) {
        console.error('Error downloading the file:', error);
      }
    },

    toggleLoadingModal(open: boolean) {
      this.loadingModalOpen = open;
    },
  },
});
